"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { chainIdsByIntegration, chainNamesByIntegration } from "@b3/chain-registry";
import { getRecentMorphoVaults, type RecentMorphoVault } from "@/lib/utils/recentSelections";
export const MORPHO_API_URL = "https://blue-api.morpho.org/graphql";
export const MORPHO_SUPPORTED_CHAIN_IDS: number[] = chainIdsByIntegration("morpho");
export const MORPHO_CHAIN_NAMES: Record<number, string> = chainNamesByIntegration("morpho");
export interface MorphoVault {
  address: string;
  name: string;
  chainId: number;
  netApy: number;
  apy: number;
  tvlUsd: number;
  assetSymbol: string;
  assetAddress: string;
  assetDecimals: number;
}
interface GraphQLVaultItem {
  address: string;
  name: string;
  chain?: {
    id: number;
  };
  asset: {
    address: string;
    symbol: string;
    decimals: number;
    price?: {
      usd: number;
    } | null;
  };
  state: {
    apy: number;
    netApy: number;
    totalAssets: string | number;
  };
}

// Fetch up to 1000 listed vaults so client-side search covers all of them.
// Morpho's GraphQL schema doesn't support a `search` parameter — filtering
// and TVL sort happen client-side in the useMemo below.
const VAULTS_QUERY = `
query GetVaults($chainIds: [Int!]!) {
  vaults(
    first: 1000
    where: {
      chainId_in: $chainIds
      listed: true
    }
  ) {
    items {
      address
      name
      chain { id }
      asset {
        address
        symbol
        decimals
        price { usd }
      }
      state {
        apy
        netApy
        totalAssets
      }
    }
  }
}
`;
const VAULT_BY_ADDRESS_QUERY = `
query VaultByAddress($addresses: [String!]!, $chainIds: [Int!]!) {
  vaults(
    where: {
      address_in: $addresses
      chainId_in: $chainIds
    }
  ) {
    items {
      address
      name
      chain { id }
      asset {
        address
        symbol
        decimals
        price { usd }
      }
      state {
        apy
        netApy
        totalAssets
      }
    }
  }
}
`;
function parseVaultItems(items: GraphQLVaultItem[], fallbackChainId?: number): MorphoVault[] {
  return items.map(item => {
    const resolvedChainId = item.chain?.id ?? fallbackChainId ?? 1;
    const totalAssetsNum = typeof item.state.totalAssets === "string" ? Number(item.state.totalAssets) : item.state.totalAssets;
    const priceUsd = item.asset.price?.usd ?? 0;
    const tvlUsd = totalAssetsNum / Math.pow(10, item.asset.decimals) * priceUsd;
    return {
      address: item.address,
      name: item.name,
      chainId: resolvedChainId,
      netApy: item.state.netApy,
      apy: item.state.apy,
      tvlUsd,
      assetSymbol: item.asset.symbol,
      assetAddress: item.asset.address,
      assetDecimals: item.asset.decimals
    };
  });
}
async function fetchMorphoVaults(chainId: number | undefined, signal?: AbortSignal): Promise<MorphoVault[]> {
  const chainIds = chainId ? [chainId] : [...MORPHO_SUPPORTED_CHAIN_IDS];
  const response = await fetch(MORPHO_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({
      query: VAULTS_QUERY,
      variables: {
        chainIds
      }
    }),
    signal
  });
  if (!response.ok) {
    throw new Error(`Morpho API error: ${response.status}`);
  }
  const json = await response.json();
  if (json.errors?.length) {
    throw new Error(json.errors[0].message || "GraphQL error");
  }
  const items: GraphQLVaultItem[] = json.data?.vaults?.items ?? [];
  const vaults = parseVaultItems(items, chainId);

  // Sort by TVL descending (client-side since Morpho API doesn't support orderBy)
  vaults.sort((a, b) => b.tvlUsd - a.tvlUsd);
  return vaults;
}

/** Fetch a single vault by its contract address (for resolving pre-set values) */
export async function fetchVaultByAddress(address: string, chainId?: number): Promise<MorphoVault | null> {
  const chainIds = chainId ? [chainId] : [...MORPHO_SUPPORTED_CHAIN_IDS];
  const variables = {
    addresses: [address.toLowerCase()],
    chainIds
  };
  const response = await fetch(MORPHO_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({
      query: VAULT_BY_ADDRESS_QUERY,
      variables
    })
  });
  if (!response.ok) return null;
  const json = await response.json();
  if (json.errors?.length) return null;
  const items: GraphQLVaultItem[] = json.data?.vaults?.items ?? [];
  const vaults = parseVaultItems(items, chainId);
  return vaults[0] ?? null;
}
interface UseMorphoVaultSearchOptions {
  /** Filter vaults to a specific chain (undefined = all supported chains) */
  chainId?: number;
  /** Whether the popover is open (controls fetching) */
  open: boolean;
}
export function useMorphoVaultSearch(t0) {
  const $ = _c(30);
  const {
    chainId,
    open
  } = t0;
  const [searchQuery, setSearchQuery] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [allVaults, setAllVaults] = useState(t1);
  const [isLoading, setIsLoading] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [recentVaults, setRecentVaults] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      status: "idle"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const fetchStatusRef = useRef(t3);
  let t4;
  if ($[3] !== chainId) {
    t4 = () => {
      const recent = getRecentMorphoVaults();
      const filtered = chainId ? recent.filter(v => v.chainId === chainId) : recent;
      setRecentVaults(filtered);
    };
    $[3] = chainId;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const refreshRecentVaults = t4;
  let t5;
  let t6;
  if ($[5] !== chainId || $[6] !== open || $[7] !== refreshRecentVaults) {
    t5 = () => {
      if (!open) {
        return;
      }
      refreshRecentVaults();
      const fs = fetchStatusRef.current;
      const isStale = fs.fetchedAt != null && Date.now() - fs.fetchedAt > 300000;
      if ((fs.status === "done" || fs.status === "fetching") && fs.chainId === chainId && !isStale) {
        return;
      }
      const controller = new AbortController();
      fetchStatusRef.current = {
        chainId,
        status: "fetching"
      };
      setIsLoading(true);
      fetchMorphoVaults(chainId, controller.signal).then(result => {
        if (!controller.signal.aborted) {
          setAllVaults(result);
          fetchStatusRef.current = {
            chainId,
            status: "done",
            fetchedAt: Date.now()
          };
        }
      }).catch(err => {
        if (!controller.signal.aborted) {
          console.error("Failed to fetch Morpho vaults:", err);
          setAllVaults([]);
          fetchStatusRef.current = {
            chainId,
            status: "error"
          };
        }
      }).finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });
      return () => {
        controller.abort();
        if (fetchStatusRef.current.status === "fetching") {
          fetchStatusRef.current = {
            status: "idle"
          };
        }
      };
    };
    t6 = [open, chainId, refreshRecentVaults];
    $[5] = chainId;
    $[6] = open;
    $[7] = refreshRecentVaults;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[10] !== open) {
    t7 = () => {
      if (!open) {
        setSearchQuery("");
      }
    };
    t8 = [open];
    $[10] = open;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  let vaults = allVaults;
  if (searchQuery) {
    let t10;
    if ($[13] !== searchQuery || $[14] !== vaults) {
      const q = searchQuery.toLowerCase();
      t10 = vaults.filter(v_0 => v_0.name.toLowerCase().includes(q) || v_0.assetSymbol.toLowerCase().includes(q) || v_0.address.toLowerCase().includes(q));
      $[13] = searchQuery;
      $[14] = vaults;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    vaults = t10;
  }
  if (!searchQuery && recentVaults.length > 0) {
    let t10;
    if ($[16] !== recentVaults) {
      t10 = new Set(recentVaults.map(_temp));
      $[16] = recentVaults;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    const recentKeys = t10;
    let t11;
    if ($[18] !== recentKeys || $[19] !== vaults) {
      let t12;
      if ($[21] !== recentKeys) {
        t12 = v_2 => !recentKeys.has(`${v_2.chainId}-${v_2.address.toLowerCase()}`);
        $[21] = recentKeys;
        $[22] = t12;
      } else {
        t12 = $[22];
      }
      t11 = vaults.filter(t12);
      $[18] = recentKeys;
      $[19] = vaults;
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    vaults = t11;
  }
  t9 = vaults;
  const filteredVaults = t9;
  let t10;
  if ($[23] !== allVaults || $[24] !== filteredVaults || $[25] !== isLoading || $[26] !== recentVaults || $[27] !== refreshRecentVaults || $[28] !== searchQuery) {
    t10 = {
      vaults: filteredVaults,
      allVaults,
      recentVaults,
      searchQuery,
      isLoading,
      setSearchQuery,
      refreshRecentVaults
    };
    $[23] = allVaults;
    $[24] = filteredVaults;
    $[25] = isLoading;
    $[26] = recentVaults;
    $[27] = refreshRecentVaults;
    $[28] = searchQuery;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}
function _temp(v_1) {
  return `${v_1.chainId}-${v_1.address.toLowerCase()}`;
}